import { useState, useEffect, useRef } from 'react';
import { send } from '@/shared/messages';
import type { QaMessage } from '@/shared/types';
import { Markdown } from './Markdown';
import { Button } from '@/components/Button';
import { Alert } from '@/components/Alert';
import { Input } from '@/components/Field';

export function QAChat({
  chapterId,
  initialHistory,
}: {
  chapterId: string;
  initialHistory: QaMessage[];
}) {
  const [messages, setMessages] = useState<QaMessage[]>(initialHistory);
  const [input, setInput] = useState('');
  const [pending, setPending] = useState(false);
  const [streamingText, setStreamingText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMessages(initialHistory);
  }, [initialHistory]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, pending, streamingText]);

  // Subscribe to streaming deltas for this chapter's QA call.
  useEffect(() => {
    function onMessage(m: unknown) {
      if (!m || typeof m !== 'object' || !('type' in m)) return;
      if ((m as { type: string }).type !== 'LLM_DELTA') return;
      const d = m as unknown as {
        surface: 'regen' | 'qa' | 'chat';
        chapterId?: string;
        text: string;
      };
      if (d.surface !== 'qa' || d.chapterId !== chapterId) return;
      setStreamingText(d.text);
    }
    chrome.runtime.onMessage.addListener(onMessage);
    return () => chrome.runtime.onMessage.removeListener(onMessage);
  }, [chapterId]);

  async function ask() {
    const q = input.trim();
    if (!q) return;
    setInput('');
    setError(null);
    const optimistic: QaMessage = {
      id: `local-${Date.now()}`,
      chapter_id: chapterId,
      role: 'user',
      content: q,
      created_at: Date.now(),
    };
    setMessages((m) => [...m, optimistic]);
    setPending(true);
    setStreamingText('');
    try {
      const r = await send({ type: 'ASK', chapterId, question: q });
      setMessages((m) => [
        ...m,
        {
          id: `local-${Date.now() + 1}`,
          chapter_id: chapterId,
          role: 'assistant',
          content: r.reply,
          created_at: Date.now(),
        },
      ]);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setPending(false);
      setStreamingText('');
    }
  }

  return (
    <div className="flex flex-col gap-2">
      <h2 className="text-sm font-semibold text-slate-700">Ask about this chapter</h2>
      <div
        ref={scrollRef}
        className="max-h-[45vh] min-h-32 overflow-y-auto rounded border border-slate-200 bg-card p-2 text-sm @md:max-h-72"
      >
        {messages.length === 0 && !pending && (
          <p className="p-2 text-xs text-slate-500">
            Ask anything about the chapter — definitions, scenarios, “why does this rule exist,”
            etc. Answers stay grounded in the source.
          </p>
        )}
        {messages.map((m) => (
          <div
            key={m.id}
            className={`my-1 rounded px-2 py-1 ${
              m.role === 'user'
                ? 'ml-6 bg-blue-50 text-slate-900 dark:bg-blue-900'
                : 'mr-6 bg-slate-100 text-slate-900'
            }`}
          >
            <div className="mb-0.5 text-[10px] uppercase tracking-wide text-slate-500">
              {m.role}
            </div>
            {m.role === 'assistant' ? (
              <Markdown source={m.content} />
            ) : (
              <div className="whitespace-pre-wrap text-sm">{m.content}</div>
            )}
          </div>
        ))}
        {pending && (
          <div className="mr-6 my-1 rounded bg-slate-100 px-2 py-1 text-sm text-slate-900">
            <div className="mb-0.5 text-[10px] uppercase tracking-wide text-slate-500">
              assistant
            </div>
            {streamingText ? (
              <Markdown source={streamingText} trailingCaret />
            ) : (
              <span className="inline-block animate-pulse text-slate-500">thinking…</span>
            )}
          </div>
        )}
      </div>
      {error && <Alert>{error}</Alert>}
      <div className="flex gap-2">
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              void ask();
            }
          }}
          placeholder="Ask a question…"
          disabled={pending}
          className="flex-1"
        />
        <Button variant="primary" onClick={ask} disabled={pending || !input.trim()}>
          Send
        </Button>
      </div>
    </div>
  );
}
